import React from "react";
import { getColumnById } from "../../config/planning";
import { PLAN_TIME_BUCKETS } from "../../lib/planBuckets";

const DAYS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];

function truncate(text, maxLen) {
  if (!text) return "";
  const t = String(text).trim();
  if (t.length <= maxLen) return t;
  return `${t.slice(0, Math.max(0, maxLen - 1)).trimEnd()}…`;
}

function resolvePlanItem({ columnId, eventId }, columns, columnDescriptors) {
  const colInfo = getColumnById(columnId, columnDescriptors);
  if (!colInfo) return null;
  const { index: colIndex } = colInfo;
  const col = columns[colIndex];
  if (!col) return null;
  let event = col.find((e) => e && e.id === eventId);
  if (!event) {
    const idx = Number(eventId);
    if (!isNaN(idx) && idx >= 0 && idx < col.length) event = col[idx];
  }
  return event ? { event, colIndex } : null;
}

/** Get day label: "Heute" for day 0, else weekday (e.g. "Fr") */
function getDayLabel(dayIndex, date) {
  if (dayIndex === 0) return "Heute";
  return date.toLocaleDateString("de-CH", { weekday: "short" });
}

/** All plan items for one day (merges legacy per-bucket canonical hours). */
function getFullDayItems(slots, day, getSlotKey) {
  const items = [];
  for (const { canonicalHour } of PLAN_TIME_BUCKETS) {
    const key = getSlotKey(day, canonicalHour);
    const slotItems = slots[key] || [];
    slotItems.forEach((ref, refIndex) => {
      items.push({ day, hour: canonicalHour, refIndex, ref });
    });
  }
  return items;
}

export default function DayBucketGrid({
  slots = {},
  columns,
  columnDescriptors = [],
  dayDates,
  getSlotKey,
  onDayClick,
  onOpenCard,
  onRemove,
}) {
  return (
    <div className="day-bucket-grid-wrapper">
      <div className="day-bucket-grid">
        {DAYS.map((day) => {
          const bucketItems = getFullDayItems(slots, day, getSlotKey);
          const resolved = bucketItems
            .map(({ day: d, hour, refIndex, ref }) => {
              const r = resolvePlanItem(ref, columns, columnDescriptors);
              return r ? { ...r, day: d, hour, refIndex } : null;
            })
            .filter(Boolean);

          return (
            <div key={day} className="day-bucket-row">
              <div className="day-bucket-day-header">
                <span className="day-bucket-day-label">{getDayLabel(day, dayDates[day])}</span>
                <span className="day-bucket-day-date">
                  {dayDates[day].toLocaleDateString("de-CH", { day: "numeric", month: "short" })}
                </span>
              </div>
              <div className="day-bucket-buckets day-bucket-buckets-full-day">
                <div
                  className="day-bucket-slot day-bucket-slot-full-day"
                  onClick={() => onDayClick(day)}
                  role="button"
                  tabIndex={0}
                  aria-label={`Zum Plan hinzufügen — ${getDayLabel(day, dayDates[day])} ${dayDates[day].toLocaleDateString("de-CH", { day: "numeric", month: "short" })}`}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onDayClick(day);
                    }
                  }}
                >
                  <div className="day-bucket-slot-content">
                    {resolved.map(({ event, colIndex, day: d, hour, refIndex }) => (
                      <div
                        key={`${colIndex}-${event?.id ?? refIndex}-${d}-${hour}`}
                        className="day-bucket-slot-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          onOpenCard(colIndex, event);
                        }}
                      >
                        {event?.image ? (
                          <img
                            className="day-bucket-slot-item-thumb"
                            src={event.image}
                            alt=""
                            loading="lazy"
                          />
                        ) : null}
                        <span className="day-bucket-slot-item-title">
                          {truncate(event?.title, 20)}
                        </span>
                        <button
                          type="button"
                          className="day-bucket-slot-item-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            onRemove(d, hour, refIndex);
                          }}
                          aria-label="Entfernen"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                    <span className="day-bucket-slot-add">+</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
